/**
 * THE SITE'S PUBLIC PLACES, GROUPED (Miles, 2026-10-07: "find the subfolders and link them in drop down like a large
 * sophisticated company would to appeal to different audiences and help users navigate the web application much much
 * easier across the web application").
 *
 * THE GSD MENUS (Miles, 2026-10-15): five groups, in this order: product, solutions, ROI, pricing, company. Each is a
 * pill in the new design's header that opens its mega-menu (D-88), and a column in both footers. Every place is a page
 * or a section of the home page (`/#…`) or of /pricing (`/pricing#…`); the /roi and /solutions addresses are the ones
 * src/config/gsdPublicPages.ts lists (tests/landing/siteNavigation.test.ts holds them equal; they are written here, not
 * imported, so that list's titles and descriptions never ride the first download with today's footer).
 *
 * One list, read by the new design's header (its menus and the phone's sheet), the new design's footer and today's
 * footer (src/components/GlobalFooter.tsx), so the three never offer different places. Plain data with no React and no
 * styles: today's footer rides the first download, the new design's header rides the shell's chunk, and both import it.
 *
 * Every page a place names is a route App.tsx declares (tests/landing/siteNavigation.test.ts reads the routes from
 * App.tsx). What each place is about, a line under its name in the header's menus, lives in
 * ./siteNavigationDescriptions.ts, which only the menus' own chunk imports. A place is known by its `id` there and in
 * the glyph maps, never by its address: two places may share one (none does today).
 */
import { MONTHLY_WU_PER_PRESTON } from './workUnits';

export type SiteNavGroupId = 'product' | 'solutions' | 'roi' | 'pricing' | 'company';

export interface SiteNavItem {
  /** The place's own name in the descriptions and the glyph maps (unique across the site's places). */
  id: string;
  to: string;
  label: string;
}

export interface SiteNavGroup {
  id: SiteNavGroupId;
  label: string;
  items: readonly SiteNavItem[];
}

export const SITE_NAV_GROUPS: readonly SiteNavGroup[] = [
  {
    id: 'product',
    label: 'product',
    items: [
      { id: 'how-it-works', to: '/#how-it-works', label: 'how it works' },
      { id: 'demo', to: '/#demo', label: 'guided demo' },
      // /integrations asks a signed-out visitor to sign in first (a ProtectedRoute in App.tsx), so the public menu
      // opens the home page's strip of the apps that connect directly (#integrations, since 2026-10-15)
      { id: 'integrations', to: '/#integrations', label: 'integrations' },
      { id: 'control', to: '/#control', label: 'security & control' },
      // "what's new" (2026-10-21): the one place for every new feature, improvement and fix, month by month. It took
      // the ROI group's "changelog / versions", whose page (the ROI report's versions) now opens it too.
      { id: 'whats-new', to: '/changelog', label: "what's new" },
    ],
  },
  {
    id: 'solutions',
    label: 'solutions',
    items: [
      { id: 'solutions-law', to: '/solutions/law', label: 'law firms' },
      { id: 'solutions-finance', to: '/solutions/finance', label: 'finance & accounting' },
      { id: 'solutions-professional', to: '/solutions/professional', label: 'other professional firms' },
    ],
  },
  {
    id: 'roi',
    label: 'ROI',
    items: [
      { id: 'roi', to: '/roi', label: 'ROI by industry' },
      { id: 'roi-calculator', to: '/roi/calculator', label: 'ROI calculator' },
      { id: 'roi-methodology', to: '/roi/methodology', label: 'methodology & assumptions' },
      { id: 'roi-data', to: '/roi/data', label: 'open data & downloads' },
    ],
  },
  {
    id: 'pricing',
    label: 'pricing',
    items: [
      { id: 'pricing', to: '/pricing', label: 'plans & price ladder' },
      { id: 'rate-card', to: '/pricing#rate-card', label: 'rate card: cost per task' },
      { id: 'capacity', to: '/pricing#capacity', label: `what ${MONTHLY_WU_PER_PRESTON.toLocaleString('en-US')} work units buy` },
      { id: 'pricing-faq', to: '/pricing#faq', label: 'pricing FAQ' },
    ],
  },
  {
    id: 'company',
    label: 'company',
    // "founders" (/team#founders) left on 2026-10-19: the team page it opened leads with them, and "team" opens it.
    // "insights" came back on 2026-10-21, its own place right after the team ("more visible on the footer and header
    // sections ... under the company drop down"): it had been a footer extra since 2026-10-15.
    items: [
      { id: 'team', to: '/team', label: 'team' },
      { id: 'insights', to: '/insights', label: 'insights' },
      { id: 'trust', to: '/trust', label: 'trust & data handling' },
      { id: 'faq', to: '/#faq', label: 'FAQ' },
    ],
  },
];

/**
 * Pages the footers keep in a group's column though the header's menus no longer name them (2026-10-15), so a page
 * that was one press from every page stays one: the menus are Miles's list, the footer is the site's index.
 */
export const SITE_FOOTER_EXTRAS: Readonly<Partial<Record<SiteNavGroupId, readonly SiteNavItem[]>>> = {
  company: [
    { id: 'about', to: '/about', label: 'about' },
    { id: 'mission', to: '/mission', label: 'mission' },
  ],
};

/** Is `to` the page at `pathname`? A link to a section of a page (`/#faq`, `/pricing#faq`) is never "the page". */
export function isSiteNavCurrent(to: string, pathname: string): boolean {
  if (to.includes('#')) return false;
  return pathname === to;
}
